<template>
  <div class="task-comments">
    <div
      v-if="GET_TASK.comments?.length"
      class="mt-3"
    >
      <h6 class="mb-2">
        {{ $t("Comments") }}
      </h6>

      <div
        v-for="comment in GET_TASK.comments"
        :key="comment.uuid"
      >
        <div class="d-flex mb-1">
          <b-avatar
            :src="getStorage(comment.author.avatar)"
            :text="getInitials(comment.author.name)"
            size="36"
            :variant="'light-primary'"
            :style="{ marginTop: '4px' }"
            class="mr-1 mt-1"
          />

          <div class="w-100">
            <div
              class="border border-2 rounded-lg"
              @click="onClick"
            >
              <div class="d-flex bg-light-secondary justify-content-between align-items-start p-1 border-bottom w-100">
                <div>
                  <h6 class="mb-0">
                    {{ comment.author.name }}
                  </h6>
                  <small class="text-secondary">{{ FORMAT_DATE(comment.updatedAt, 'Do MMM, YYYY') }}</small>
                </div>

                <div class="d-flex">
                  <b-badge
                    v-if="comment.createdAt !== comment.updatedAt"
                    variant="light-secondary"
                  >
                    <feather-icon
                      icon="Edit2Icon"
                      size="14"
                    />
                    {{ $t("Edited") }}
                  </b-badge>

                  <b-dropdown
                    v-if="comment.author.uuid === self.uuid"
                    right
                    variant="link"
                    class="p-0"
                    no-caret
                  >
                    <template #button-content>
                      <feather-icon
                        icon="MoreVerticalIcon"
                        size="16"
                      />
                    </template>

                    <b-dropdown-item
                      v-if="editingCommentUid !== comment.uuid"
                      @click="() => onEditComment(comment)"
                    >
                      <feather-icon
                        icon="Edit2Icon"
                        class="mr-1"
                        size="14"
                      />
                      {{ $t("Edit") }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-b-modal.delete-comment-confirm-modal
                      variant="danger"
                      @click="commentToDeleteUid = comment.uuid"
                    >
                      <feather-icon
                        icon="Trash2Icon"
                        class="mr-1"
                        size="14"
                      />
                      {{ $t("Delete") }}
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </div>

              <markdown-it-vue
                v-if="editingCommentUid !== comment.uuid"
                :key="comment.uuid"
                class="markdown-body p-1"
                :content="processMentionContent(comment.content, comment.mentionedUsers)"
                :options="markdownOptions"
              />
            </div>

            <div v-if="editingCommentUid === comment.uuid">
              <markdown-editor
                v-model="editingContent"
                :placeholder="$t('Edit your comment...')"
                :min-height="'100px'"
                :toolbar="['bold', 'italic', 'link', 'mention', 'emoji']"
                :board-members="boardMembers"
                :mentions="comment.mentionedUsers"
                class="flex-grow-1"
              />

              <div class="float-right">
                <b-button
                  variant="secondary"
                  class="mt-1 mx-1"
                  @click="editingCommentUid = null; editingContent = ''"
                >
                  {{ $t("Cancel") }}
                </b-button>
                <b-button
                  variant="success"
                  class="mt-1"
                  :disabled="!editingContent.trim() || isProcessing"
                  @click="updateComment(comment.uuid)"
                >
                  {{ $t("Save") }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Comment -->
    <div class="d-flex align-items-center mt-3 mb-1">
      <b-avatar
        :src="getStorage(self.avatar)"
        :text="getInitials(self.name)"
        size="36"
        :variant="'light-primary'"
        :style="{ marginTop: '4px' }"
        class="mr-1"
      />
      <h6 class="mb-0">
        {{ $t("Add a comment") }}
      </h6>
    </div>

    <markdown-editor
      v-model="comment"
      :placeholder="$t('Add a comment...')"
      :min-height="'50px'"
      :toolbar="['bold', 'italic', 'link', 'mention', 'emoji']"
      :board-members="boardMembers"
      class="flex-grow-1"
      @submit="addComment"
    />

    <b-button
      variant="success"
      class="mt-2 float-right"
      :disabled="!comment.trim() || isProcessing"
      @click="addComment"
    >
      {{ $t('Comment') }}
    </b-button>

    <!-- Delete Comment Confirmation Modal -->
    <confirm-modal
      modal="delete-comment-confirm-modal"
      :title="$t('Delete Comment')"
      :description="$t('Are you sure you want to delete this comment? This action cannot be undone.')"
      :ok-title="$t('Delete')"
      ok-variant="danger"
      @confirm="deleteTaskComment({ commentUid: commentToDeleteUid })"
      @hidden="commentToDeleteUid = null"
    />

    <!-- Edit in Progress Modal -->
    <confirm-modal
      modal="edit-in-progress-modal"
      :title="$t('Discard Changes')"
      :alert="$t('All unsaved changes will be lost. Do you want to continue?')"
      alert-variant="danger"
      :ok-title="$t('Discard')"
      ok-variant="danger"
      @confirm="() => { editingCommentUid = null; onEditComment(tempEditingComment) }"
      @cancel="tempEditingComment = null"
    />

    <CoolLightBox
      :items="fetchImages || []"
      :index="coolLightBoxIndex"
      @close="coolLightBoxIndex = null"
    />
  </div>
</template>

<script>
import { getUserData } from '@/auth/utils'
import {
  BAvatar, BButton, BBadge, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import MarkdownItVue from 'markdown-it-vue'
import ConfirmModal from '@/views/Wallet/components/reusables/ConfirmModal.vue'
import { mapActions, mapGetters } from 'vuex'
import CoolLightBox from 'vue-cool-lightbox'
import {
  fetchImagesFromMarkdown, processMentionContent, processMentionContentForEditor, removeNameFromMentions,
} from '../markdown'
import MarkdownEditor from '../MarkdownEditor.vue'

export default {
  name: 'TaskComments',
  components: {
    BAvatar,
    BButton,
    BBadge,
    BDropdown,
    BDropdownItem,
    ConfirmModal,
    MarkdownEditor,
    MarkdownItVue,
    CoolLightBox,
  },
  data() {
    return {
      processMentionContent,
      processMentionContentForEditor,
      self: getUserData(),
      comment: '',
      tempEditingComment: null,
      editingCommentUid: null,
      commentToDeleteUid: null,
      editingContent: '',
      isProcessing: false,
      markdownOptions: {
        markdownIt: {
          linkify: true,
          html: true,
          breaks: true,
        },
        image: {
          viewer: false,
        },
      },
      coolLightBoxIndex: null,
    }
  },
  computed: {
    ...mapGetters('boards', ['GET_TASK', 'GET_CURRENT_BOARD']),
    boardMembers() {
      return this.GET_CURRENT_BOARD?.members?.data || []
    },
    fetchImages() {
      return this.GET_TASK.comments.reduce((images, comment) => {
        const commentImages = fetchImagesFromMarkdown(comment.content)
        return images.concat(commentImages)
      }, [])
    },
  },
  methods: {
    ...mapActions('boards', ['addTaskComment', 'updateTaskComment', 'deleteTaskComment']),
    onEditComment(comment) {
      if (this.editingCommentUid) {
        this.tempEditingComment = comment
        this.$bvModal.show('edit-in-progress-modal')
      } else {
        this.tempEditingComment = null
        this.editingCommentUid = comment.uuid
        this.editingContent = this.processMentionContentForEditor(comment.content, comment.mentionedUsers)
      }
    },
    async updateComment(commentUid) {
      this.isProcessing = true
      try {
        const response = await this.updateTaskComment({
          commentUid,
          content: removeNameFromMentions(this.editingContent.trim()),
        })

        this.editingCommentUid = null
        this.editingContent = ''
        this.showSuccess(response.message)
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.isProcessing = false
      }
    },
    async addComment() {
      this.isProcessing = true
      try {
        const response = await this.addTaskComment({
          taskUid: this.GET_TASK.uuid,
          input: {
            content: removeNameFromMentions(this.comment.trim()),
          },
        })

        this.comment = ''
        this.showSuccess(response.message)
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.isProcessing = false
      }
    },
    onClick({ target }) {
      if (target.tagName === 'IMG') {
        const src = target.getAttribute('src')
        const index = this.fetchImages.findIndex(img => img === src)

        if (index !== -1) {
          this.coolLightBoxIndex = index
        }
      }
    },
  },
}
</script>

<style lang="scss">
.task-comments {
  .dropdown-toggle {
    padding: 0;
  }

  img {
    width: 100% !important;
  }
}
</style>
